import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Document } from 'src/app/models/document';
import { DocumentService } from 'src/app/services/document.service';
import { ToastrService } from 'ngx-toastr';
import { WorkflowContextService, WorkflowService, Utilities } from 'src/app/services';
import { PreviousSystemDataImportRequest } from 'src/app/models/previous-system-data';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';

@Component({
  selector: 'wm-settings-previous-system',
  standalone: false,
  templateUrl: './settings-previous-system.component.html',
  styleUrls: ['./settings-previous-system.component.scss']
})
export class SettingsPreviousSystemComponent implements OnInit, OnDestroy {
  @ViewChild('dataFileInput') dataFileInput: ElementRef;
  @ViewChild('indexFileInput') indexFileInput: ElementRef;

  form: UntypedFormGroup;
  fileCompleteSub: Subscription;
  fileAddedSub: Subscription;
  clientId: string;
  newUploadKey: string;
  activeUploadKey: string = null;
  uploadData = false;
  uploadDataAttachments = false;
  uploadRequest: PreviousSystemDataImportRequest;
  
  selectedDataFile: File | null = null;
  selectedIndexFile: File | null = null;
  dataFileName: string = '';
  indexFileName: string = '';
  isUploading = false;

  private dataFileUrl: string | null = null;
  private indexFileUrl: string | null = null;

  confirmData: CommonConfirmationModalData = {
    open: false,
    modalDescp: '',
    modalSubDescp: '',
    yesNoBtn: true,
    inTime: 300,
    outTime: 200,
    status: 'default'
  };

  private pendingAction: 'clear' | 'data' | 'index' | null = null;

  constructor(
    private _documentSvc: DocumentService,
    private _fb: UntypedFormBuilder,
    private _context: WorkflowContextService,
    private _toastrSvc: ToastrService,
    private _workflowSvc: WorkflowService
  ) {
    this.clientId = this._context.client.id;
  }

  ngOnInit() {
    this.newUploadKey = Utilities.generateId();

    this.form = this._fb.group({
      uploadData: [''],
      uploadDataAttachments: [''],
      clearExisting: [''],
      hasRelevantSettingsSpecified: ['']
    });

    this.fileAddedSub = this._documentSvc.filesAdded.subscribe(
      (files: { files: any; key: string }) => {
        this.activeUploadKey = files.key;
        this.newUploadKey = Utilities.generateId();
      }
    );

    this.fileCompleteSub = this._documentSvc.fileUploadComplete.subscribe(
      (files: { files: any; key: string }) => {
      }
    );

    this.initializeRequest();
  }

  ngOnDestroy(): void {
    if (this.fileCompleteSub) {
      this.fileCompleteSub.unsubscribe();
    }

    if (this.fileAddedSub) {
      this.fileAddedSub.unsubscribe();
    }

    this.revokeDataUrl();
    this.revokeIndexUrl();
  }
  
  onDataFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.selectedDataFile = file;
      this.dataFileName = file.name;
      this.createDataUrl(file);
      this.updateHasRelevantSettingsSpecified();
    }
  }
  
  onIndexFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.selectedIndexFile = file;
      this.indexFileName = file.name;
      this.createIndexUrl(file);
      this.updateHasRelevantSettingsSpecified();
    }
  }
  
  private createDataUrl(file: File) {
    this.revokeDataUrl();
    try {
      this.dataFileUrl = URL.createObjectURL(file);
    } catch (err) {
      console.warn('Failed to create blob url for data file', err);
      this.dataFileUrl = null;
    }
  }

  private createIndexUrl(file: File) {
    this.revokeIndexUrl();
    try {
      this.indexFileUrl = URL.createObjectURL(file);
    } catch (err) {
      console.warn('Failed to create blob url for index file', err);
      this.indexFileUrl = null;
    }
  }

  private revokeDataUrl() {
    if (this.dataFileUrl) {
      try {
        URL.revokeObjectURL(this.dataFileUrl);
      } catch (_) {}
      this.dataFileUrl = null;
    }
  }

  private revokeIndexUrl() {
    if (this.indexFileUrl) {
      try {
        URL.revokeObjectURL(this.indexFileUrl);
      } catch (_) {}
      this.indexFileUrl = null;
    }
  }

  viewSelectedFile(type: 'data' | 'index') {
    if (type === 'data' && this.dataFileUrl) {
      window.open(this.dataFileUrl, '_blank');
    } else if (type === 'index' && this.indexFileUrl) {
      window.open(this.indexFileUrl, '_blank');
    } else {
      if (type === 'data' && this.selectedDataFile) {
        const tmp = URL.createObjectURL(this.selectedDataFile);
        window.open(tmp, '_blank');
        setTimeout(() => URL.revokeObjectURL(tmp), 5000);
      } else if (type === 'index' && this.selectedIndexFile) {
        const tmp = URL.createObjectURL(this.selectedIndexFile);
        window.open(tmp, '_blank');
        setTimeout(() => URL.revokeObjectURL(tmp), 5000);
      } else {
        this._toastrSvc.info('No file available to view.');
      }
    }
  }

  removeDataFile() {
    this.selectedDataFile = null;
    this.dataFileName = '';
    this.revokeDataUrl();
    if (this.dataFileInput) {
      this.dataFileInput.nativeElement.value = '';
    }
    if (this.uploadRequest.tabularData && this.uploadRequest.tabularData.length > 0) {
      this.uploadRequest.tabularData = [];
    }
    this.updateHasRelevantSettingsSpecified();
  }
  
  removeIndexFile() {
    this.selectedIndexFile = null;
    this.indexFileName = '';
    this.revokeIndexUrl();
    if (this.indexFileInput) {
      this.indexFileInput.nativeElement.value = '';
    }
    this.uploadRequest.attachmentIndexFile = null;
    this.updateHasRelevantSettingsSpecified();
  }

  checkUpload() {
    if (this.uploadRequest.clearExisting) {
      this.pendingAction = 'clear';
      this.confirmData = {
        ...this.confirmData,
        open: true,
        modalDescp: 'Clear Previously Imported Records',
        modalSubDescp: 'Are you sure you want to delete any previously imported data and attachments and replace with new data? This action cannot be undone.',
        yesNoBtn: true,
        replaceYesWith: 'YES, PROCEED',
        replaceNoWith: 'CANCEL'
      };
    } else {
      this.importData();
    }
  }

  requestDeleteFile(type: 'data' | 'index') {
    this.pendingAction = type;
    const fileLabel = type === 'data' ? 'selected data file' : 'selected index file';
    this.confirmData = {
      ...this.confirmData,
      open: true,
      modalDescp: `Delete ${fileLabel}`,
      modalSubDescp: `Are you sure you want to delete the ${fileLabel}? This action cannot be undone.`,
      yesNoBtn: true,
      replaceYesWith: 'DELETE',
      replaceNoWith: 'CANCEL'
    };
  }

  onConfirmModalYes() {
    this.confirmData = { ...this.confirmData, open: false };

    if (this.pendingAction === 'data') {
      this.removeDataFile();
    } else if (this.pendingAction === 'index') {
      this.removeIndexFile();
    } else if (this.pendingAction === 'clear') {
      this.importData();
    }

    this.pendingAction = null;
  }

  onConfirmModalNo() {
    this.confirmData = { ...this.confirmData, open: false };
    this.pendingAction = null;
  }

  importData() {
    if (this.isUploading) return;
    
    this.isUploading = true;
    
    this.uploadRequest.uploadKey = this.activeUploadKey || this.newUploadKey;
    
    if (!this.uploadData) {
      this.uploadRequest.tabularData = [];
    } else if (this.selectedDataFile) {
      const document = new Document({
        name: this.selectedDataFile.name,
      });
      this.uploadRequest.tabularData = [document];
    }

    if (!this.uploadDataAttachments) {
      this.uploadRequest.attachmentIndexFile = null;
    } else if (this.selectedIndexFile) {
      const document = new Document({
        name: this.selectedIndexFile.name,
      });
      this.uploadRequest.attachmentIndexFile = document;
    }

    this._workflowSvc
      .importPreviousSystemData(this.uploadRequest)
      .subscribe(
        response => {
          this.isUploading = false;
          if (response.success) {
            this.clearUploadedFiles();
            this._toastrSvc.success(response.message);
            this.initializeRequest();
          } else {
            this._toastrSvc.error(response.message);
          }
        },
        error => {
          this.isUploading = false;
          this._toastrSvc.error(`Import failed: ${error.message}`);
        }
      );
  }

  clearUploadedFiles() {
    this.selectedDataFile = null;
    this.selectedIndexFile = null;
    this.dataFileName = '';
    this.indexFileName = '';
    this.revokeDataUrl();
    this.revokeIndexUrl();
    
    if (this.dataFileInput) {
      this.dataFileInput.nativeElement.value = '';
    }
    
    if (this.indexFileInput) {
      this.indexFileInput.nativeElement.value = '';
    }
  }

  initializeRequest() {
    this.uploadData = false;
    this.uploadDataAttachments = false;
    this.uploadRequest = new PreviousSystemDataImportRequest({
      clientId: this.clientId || this._context.client.id,
      requestedByUserId: this._context.user.id,
      tabularData: [],
      clearExisting: false
    });
    
    this.form.patchValue({
      uploadData: false,
      uploadDataAttachments: false,
      clearExisting: false
    });
    
    this.updateHasRelevantSettingsSpecified();
  }

  hasRelevantSettingsSpecified(): ValidationErrors | null {
    if (!this.uploadData && !this.uploadDataAttachments) {
      return { noToggleSelected: true };
    }

    if (this.uploadData) {
      if (!this.selectedDataFile) {
        return { tabularDataFileSelected: true };
      } 
    }

    if (this.uploadDataAttachments) {
      if (!this.selectedIndexFile) {
        return { attachmentIndexFileSelected: true };
      } 
    }

    if (this.uploadRequest) {
      if (this.uploadRequest.clearExisting && !this.selectedDataFile && !this.selectedIndexFile) {
        return { cannotReplaceData: true };
      }
    }

    return null;
  }

  updateHasRelevantSettingsSpecified() {
    const validator = this.hasRelevantSettingsSpecified.bind(this);
    this.form.get('hasRelevantSettingsSpecified')?.setValidators(validator);
    this.form.get('hasRelevantSettingsSpecified')?.updateValueAndValidity();
  }

  onUploadDataChange(value: boolean) {
    this.uploadData = value;
    this.updateHasRelevantSettingsSpecified();
  }

  onUploadDataAttachmentsChange(value: boolean) {
    this.uploadDataAttachments = value;
    this.updateHasRelevantSettingsSpecified();
  }

  onClearExistingChange(value: boolean) {
    this.uploadRequest.clearExisting = value;
    this.updateHasRelevantSettingsSpecified();
  }

  isFormValidForUpload(): boolean {
    const errors = this.hasRelevantSettingsSpecified();
    return errors === null;
  }
}